/**
 * placement.ts — the Ausschreibungs-/Placement contract (P2 Placement Slice 4).
 *
 * The Placement is the top object of the Offert-/Antragsprozess (Prozesskonzept P2 §1/§3/§4):
 * one per Ausschreibungsvorgang, n:1 Kunde via a (subject_type, subject_id) value link and 0/1:1
 * Police (NULL for Neugeschaeft). This module is the SINGLE SOURCE OF TRUTH for:
 *   - the status machine (§10.1) + its transition table + the one reusable guard,
 *   - the Anlass (T1-T5) triggers and their Referenzpolice rule (§3),
 *   - the closure contract (§10.6) + the comparison result vocabulary,
 *   - the 6-tab detail shell order (§7) + the placeholder label per downstream slice,
 *   - the Liste/Pipeline column model + the KPI aggregation,
 *   - the Sparte (Branchencode) leaf rule, and the domain-event names,
 *   - the wire shapes (list item, detail, create body, abbrechen body, KPIs).
 *
 * The api owns every decision (validation, number allocation, gating); the web only renders +
 * triggers. Pure module — no side effects, no imports beyond the IGB2B family helper.
 *
 * [Source: docs/product/p2-slices/slice-4-placement-core-design.md §2/§3]
 * [Source: packages/shared/src/police-risiko.ts] (triad + labelled-enum + helper idioms)
 */

import { isIgb2bFamilyHead } from './police-risiko';
import type { PflichtdeckungWire } from './placement-snapshot';
import type { PlacementAbschlussArt } from './placement-closure';
import type { PageMeta } from './pagination';
import { encodeKeysetCursor, parseKeysetCursor } from './pagination';

// ── Subject types ────────────────────────────────────────────────────────────

/** The two customer subject kinds a Placement may reference (never `invoice`). */
export const PLACEMENT_SUBJECT_TYPES = ['person', 'firma'] as const;
export type PlacementSubjectType = (typeof PLACEMENT_SUBJECT_TYPES)[number];

// ── Status machine (§10.1) ────────────────────────────────────────────────────

/** The 9 Placement statuses (snake_case; byte-identical to the 0145 CHECK). */
export const PLACEMENT_STATUS = {
  ENTWURF: 'entwurf',
  RISIKOPROFIL_ERFASST: 'risikoprofil_erfasst',
  VORGABEN_ERFASST: 'vorgaben_erfasst',
  AUSGESCHRIEBEN: 'ausgeschrieben',
  VERGLICHEN: 'verglichen',
  EMPFOHLEN: 'empfohlen',
  ENTSCHIEDEN: 'entschieden',
  ABGESCHLOSSEN: 'abgeschlossen',
  ABGEBROCHEN: 'abgebrochen',
} as const;

export type PlacementStatus = (typeof PLACEMENT_STATUS)[keyof typeof PLACEMENT_STATUS];

/** All 9 status values in funnel order (abgebrochen last). */
export const PLACEMENT_STATUS_VALUES: readonly PlacementStatus[] = [
  'entwurf',
  'risikoprofil_erfasst',
  'vorgaben_erfasst',
  'ausgeschrieben',
  'verglichen',
  'empfohlen',
  'entschieden',
  'abgeschlossen',
  'abgebrochen',
];

/** Display labels (German domain terms). */
export const PLACEMENT_STATUS_LABELS: Record<PlacementStatus, string> = {
  entwurf: 'Entwurf',
  risikoprofil_erfasst: 'Risikoprofil erfasst',
  vorgaben_erfasst: 'Vorgaben erfasst',
  ausgeschrieben: 'Ausgeschrieben',
  verglichen: 'Verglichen',
  empfohlen: 'Empfohlen',
  entschieden: 'Entschieden',
  abgeschlossen: 'Abgeschlossen',
  abgebrochen: 'Abgebrochen',
};

/**
 * The legal forward transitions (§10.1). `abbrechen` is the only transition Slice 4 actually
 * drives (from entwurf or entschieden); the others are advanced by their owning slices via
 * `transitionTx`. Both terminal states have no outgoing edge.
 */
export const PLACEMENT_STATUS_TRANSITIONS: Record<PlacementStatus, readonly PlacementStatus[]> = {
  entwurf: ['risikoprofil_erfasst', 'abgebrochen'],
  risikoprofil_erfasst: ['vorgaben_erfasst'],
  vorgaben_erfasst: ['ausgeschrieben'],
  ausgeschrieben: ['verglichen'],
  verglichen: ['empfohlen'],
  // E13 (Slice 12): Kunde lehnt alle Offerten ab -> zurueck zur Versichererauswahl; the ONLY
  // backward edge, taken by CustomerDecisionCore only. The forward edge is empfohlen -> entschieden.
  empfohlen: ['entschieden', 'ausgeschrieben'],
  entschieden: ['abgeschlossen', 'abgebrochen'],
  abgeschlossen: [],
  abgebrochen: [],
};

/** The two terminal statuses (no outgoing transition). */
export const PLACEMENT_TERMINAL_STATUSES: readonly PlacementStatus[] = ['abgeschlossen', 'abgebrochen'];

/** Is the `to` status a legal next step from `from`? The single guard for every transition. */
export function canTransitionPlacement(from: PlacementStatus, to: PlacementStatus): boolean {
  return PLACEMENT_STATUS_TRANSITIONS[from].includes(to);
}

/**
 * The 0-based funnel step of a status (entwurf=0 … abgeschlossen=7); `abgebrochen` = -1 (it is
 * off the funnel, so a progress bar renders no filled step). Used by the Fortschritt bar.
 */
export function placementStatusStep(status: PlacementStatus): number {
  if (status === 'abgebrochen') return -1;
  return [
    'entwurf',
    'risikoprofil_erfasst',
    'vorgaben_erfasst',
    'ausgeschrieben',
    'verglichen',
    'empfohlen',
    'entschieden',
    'abgeschlossen',
  ].indexOf(status);
}

/** The number of funnel steps (entwurf … abgeschlossen) — the Fortschritt bar's denominator. */
export const PLACEMENT_STATUS_STEP_COUNT = 8;

// ── Anlass (T1-T5 triggers, §3) ────────────────────────────────────────────────

/** The 5 Platzierungs-Anlaesse (triggers T1-T5). */
export const PLACEMENT_ANLASS = {
  NEUGESCHAEFT: 'neugeschaeft',
  ERNEUERUNG: 'erneuerung',
  VERTRAGSAENDERUNG: 'vertragsaenderung',
  MARKTCHECK: 'marktcheck',
  BROKER_INITIATIVE: 'broker_initiative',
} as const;

export type PlacementAnlass = (typeof PLACEMENT_ANLASS)[keyof typeof PLACEMENT_ANLASS];

export const PLACEMENT_ANLASS_VALUES: readonly PlacementAnlass[] = [
  'neugeschaeft',
  'erneuerung',
  'vertragsaenderung',
  'marktcheck',
  'broker_initiative',
];

/** Labels carry the T1-T5 trigger numbering (concept §3). */
export const PLACEMENT_ANLASS_LABELS: Record<PlacementAnlass, string> = {
  neugeschaeft: 'T1 Neugeschaeft',
  erneuerung: 'T2 Erneuerung',
  vertragsaenderung: 'T3 Vertragsaenderung',
  marktcheck: 'T4 Marktcheck',
  broker_initiative: 'T5 Broker-Initiative',
};

/** Is a Referenzpolice required / forbidden / optional for this Anlass (§3 CHECK + 400)? */
export function placementAnlassErlaubtPolice(anlass: PlacementAnlass): 'required' | 'forbidden' | 'optional' {
  switch (anlass) {
    case 'neugeschaeft':
      return 'forbidden';
    case 'erneuerung':
    case 'vertragsaenderung':
      return 'required';
    case 'marktcheck':
    case 'broker_initiative':
      return 'optional';
  }
}

// ── Branchencode (Sparte) leaf rule ────────────────────────────────────────────

/**
 * A Placement's Branchencode must be a LEAF IGB2B code, not a family head (a Placement is
 * ausgeschrieben for one concrete Sparte). The lone exception is `60000` (Diverse/Sonstige),
 * which is a usable catch-all leaf even though it is numerically a family head.
 */
export function isPlacementBranchencodeErlaubt(code: string): boolean {
  return !isIgb2bFamilyHead(code) || code === '60000';
}

// ── Closure (§10.6 / §8.9) ─────────────────────────────────────────────────────

/** The closure lifecycle (NEU V3.0): offen on create, erledigt on close (Slice 15). */
export const PLACEMENT_CLOSURE_STATUS = {
  OFFEN: 'offen',
  ERLEDIGT: 'erledigt',
} as const;

export type PlacementClosureStatus =
  (typeof PLACEMENT_CLOSURE_STATUS)[keyof typeof PLACEMENT_CLOSURE_STATUS];

export const PLACEMENT_CLOSURE_STATUS_VALUES: readonly PlacementClosureStatus[] = ['offen', 'erledigt'];

export const PLACEMENT_CLOSURE_STATUS_LABELS: Record<PlacementClosureStatus, string> = {
  offen: 'Offen',
  erledigt: 'Erledigt',
};

/** The documented comparison outcome on close (deckungsgleich vs abweichung). */
export const PLACEMENT_COMPARISON_RESULT = {
  DECKUNGSGLEICH: 'deckungsgleich',
  ABWEICHUNG: 'abweichung',
} as const;

export type PlacementComparisonResult =
  (typeof PLACEMENT_COMPARISON_RESULT)[keyof typeof PLACEMENT_COMPARISON_RESULT];

export const PLACEMENT_COMPARISON_RESULT_VALUES: readonly PlacementComparisonResult[] = [
  'deckungsgleich',
  'abweichung',
];

// ── Detail tab shell (§7) ──────────────────────────────────────────────────────

/** The 6 fixed Placement-detail tabs (fixed order; each owned by one downstream slice). */
export const PLACEMENT_TAB = {
  RISIKOPROFIL: 'risikoprofil',
  SOLL_DECKUNG: 'soll_deckung',
  SUBMISSIONS: 'submissions',
  OFFERTEN: 'offerten',
  UNDERWRITING: 'underwriting',
  HISTORIE: 'historie',
} as const;

export type PlacementTab = (typeof PLACEMENT_TAB)[keyof typeof PLACEMENT_TAB];

/** Tab order — the shell renders this exact sequence; later slices never reshape it. */
export const PLACEMENT_TAB_VALUES: readonly PlacementTab[] = [
  'risikoprofil',
  'soll_deckung',
  'submissions',
  'offerten',
  'underwriting',
  'historie',
];

/**
 * Per-tab display label + the slice that will replace its placeholder body. Slice 4 renders
 * every tab as `<PlacementTabPlaceholder>` showing "folgt (Slice N)" from this map; each later
 * slice swaps exactly one `case` body for its real content.
 */
export const PLACEMENT_TAB_SLICE: Record<PlacementTab, { label: string; slice: number }> = {
  risikoprofil: { label: 'Risikoprofil', slice: 5 },
  soll_deckung: { label: 'Soll-Deckung', slice: 5 },
  submissions: { label: 'Offertanfragen', slice: 8 },
  offerten: { label: 'Offerten', slice: 10 },
  underwriting: { label: 'Underwriting', slice: 14 },
  historie: { label: 'Historie', slice: 15 },
};

// ── Liste / Pipeline model (§7.3) ──────────────────────────────────────────────

/** One Pipeline (Kanban) column: a label + the statuses it groups. Covers the 8 funnel statuses. */
export interface PlacementPipelineSpalte {
  key: string;
  label: string;
  statuses: readonly PlacementStatus[];
}

/** The Pipeline columns (abgebrochen placements are shown off-pipeline, never a column). */
export const PLACEMENT_PIPELINE_SPALTEN: readonly PlacementPipelineSpalte[] = [
  { key: 'entwurf', label: 'Entwurf', statuses: ['entwurf'] },
  { key: 'vorbereitung', label: 'In Vorbereitung', statuses: ['risikoprofil_erfasst', 'vorgaben_erfasst'] },
  { key: 'ausgeschrieben', label: 'Ausgeschrieben', statuses: ['ausgeschrieben'] },
  { key: 'vergleich', label: 'Vergleich & Empfehlung', statuses: ['verglichen', 'empfohlen'] },
  { key: 'entschieden', label: 'Entschieden', statuses: ['entschieden'] },
  { key: 'abgeschlossen', label: 'Abgeschlossen', statuses: ['abgeschlossen'] },
];

// ── Domain events (§0.8) ───────────────────────────────────────────────────────

/** The in-process domain-event names (carried on the activity row; no Kafka). */
export const PLACEMENT_EVENTS = {
  CREATED: 'placement.created',
  CANCELLED: 'placement.cancelled',
  // P2 Placement Slice 7 — Versicherer für die Ausschreibung ausgewählt (Prozessschritt 5).
  CARRIERS_SELECTED: 'placement.carriers_selected',
  // P2 Placement Slice 15 — Policenkontrolle + P1-Feedback + Abschluss (Prozessschritt 21, P1 steps 6/14/16).
  CLOSED: 'placement.closed',
  POLICY_REPORTED: 'policy.control.reported_to_placement',
  POLICY_CONTROL_COMPLETED: 'placement.policy_control.completed',
  POLICY_CONTROL_DISCREPANCY: 'policy.control.discrepancy_detected',
  POLICY_CONTROL_CLEARED: 'placement.policy_control.cleared',
  POLICY_DELIVERED: 'placement.policy.delivered_to_customer',
  POLICY_SHELL_REPLACED: 'placement.policy_shell.replaced',
  POLICY_RECONCILE_CONFLICT: 'placement.policy_reconcile.conflict',
  // P2 hardening 05 — the Eingang-visible Systemanalyse finding + the broker's reply to the insurer.
  POLICY_CONTROL_SYSTEMANALYSE: 'placement.policy_control.systemanalyse',
  POLICY_RUECKMELDUNG_SENT: 'placement.policy_control.rueckmeldung_sent',
} as const;

export type PlacementEvent = (typeof PLACEMENT_EVENTS)[keyof typeof PLACEMENT_EVENTS];

// ── Wire types ──────────────────────────────────────────────────────────────────

/** The 1:1 closure row (NEU V3.0, §10.6). */
export interface PlacementClosureWire {
  status: PlacementClosureStatus;
  start_zeitpunkt: string;
  end_zeitpunkt: string | null;
  resulting_policy_id: string | null;
  resulting_policy_number: string | null;
  comparison_result: PlacementComparisonResult | null;
  comparison_details_ref: string | null;
  comparison_documented_at: string | null;
  // P2 Placement Slice 15 — Abschluss-Art + Kundenzustellung.
  abschluss_art: PlacementAbschlussArt | null;
  policy_zugestellt_at: string | null;
  policy_zustellung_channel: 'portal' | 'email' | null;
}

/** One row in the Kunde Ausschreibungen list (the join read). */
export interface PlacementListItem {
  id: string;
  placement_number: string;
  subject_type: PlacementSubjectType;
  subject_id: string;
  /**
   * The customer's display name (person: "last_name first_name"; firma: name), resolved in SQL
   * so the firm-wide list (`GET /placements` without a subject) is readable. The api always sends
   * it (null when the person/firma row is soft-deleted or gone); optional in the type only so the
   * existing per-customer web fixtures stay valid.
   */
  subject_name?: string | null;
  policy_id: string | null;
  policy_number: string | null;
  carrier_name: string | null;
  insurance_object_ref: string | null;
  branchencode: string;
  branchen_label: string;
  anlass: PlacementAnlass;
  status: PlacementStatus;
  status_changed_at: string;
  closure_status: PlacementClosureStatus;
  closure_start_zeitpunkt: string;
  closure_end_zeitpunkt: string | null;
  gewuenschter_beginn: string | null;
  betreuer_user_id: string;
  betreuer_name: string | null;
  created_at: string;
  updated_at: string;
}

/** The full Placement detail (list item + the fields only the detail screen needs). */
export interface PlacementDetail extends PlacementListItem {
  abbruch_grund: string | null;
  abgebrochen_at: string | null;
  created_by: string | null;
  closure: PlacementClosureWire;
  /** The legal next statuses from the current one (`canTransitionPlacement`-derived). */
  erlaubte_transitionen: PlacementStatus[];
  /** The E12 Pflichtdeckung-Warnflag state (P2 Slice 5); `unbestaetigt_kritisch` is Tab-2 only. */
  pflichtdeckung: Omit<PflichtdeckungWire, 'unbestaetigt_kritisch'>;
}

/** The 5 list KPIs (a funnel over closure + status). */
export interface PlacementListKpis {
  offen: number;
  in_vorbereitung: number;
  beim_kunden: number;
  erledigt: number;
  abgebrochen: number;
}

/**
 * The Ausschreibungen list read response. Two scopes share it:
 *   - per customer (`?subject_type&subject_id`): echoes the subject;
 *   - firm-wide (no subject in the query): every placement the caller may see in the tenant,
 *     `subject_type` + `subject_id` are null and each item carries its `subject_name`.
 */
export interface PlacementListResponse {
  subject_type: PlacementSubjectType | null;
  subject_id: string | null;
  items: PlacementListItem[];
  kpis: PlacementListKpis;
}

/** POST /placements body (the «Neues Placement» dialog). */
export interface PlacementCreateBody {
  subject_type: PlacementSubjectType;
  subject_id: string;
  anlass: PlacementAnlass;
  policy_id?: string | null;
  branchencode?: string | null;
  insurance_object_ref?: string | null;
  gewuenschter_beginn?: string | null;
}

/** POST /placements/:id/abbrechen body. */
export interface PlacementAbbrechenBody {
  grund: string;
}

// ── KPI aggregation (pure; reused by the api service + the web mock) ────────────

/**
 * Aggregate the 5 list KPIs from the rows. The funnel:
 *   - in_vorbereitung = status ∈ {entwurf, risikoprofil_erfasst, vorgaben_erfasst}
 *   - beim_kunden     = status ∈ {ausgeschrieben, verglichen, empfohlen, entschieden}
 *   - erledigt        = closure_status = erledigt
 *   - abgebrochen     = status = abgebrochen
 *   - offen           = closure offen AND not abgebrochen (the active work in flight)
 */
export function placementKpisFromItems(items: readonly PlacementListItem[]): PlacementListKpis {
  const kpis: PlacementListKpis = { offen: 0, in_vorbereitung: 0, beim_kunden: 0, erledigt: 0, abgebrochen: 0 };
  for (const it of items) {
    // abgebrochen is off the funnel — a cancelled row counts ONLY as abgebrochen, never erledigt too.
    if (it.status === 'abgebrochen') {
      kpis.abgebrochen += 1;
      continue;
    }
    if (it.closure_status === 'erledigt') kpis.erledigt += 1;
    if (it.closure_status === 'offen') kpis.offen += 1;
    if (it.status === 'entwurf' || it.status === 'risikoprofil_erfasst' || it.status === 'vorgaben_erfasst') {
      kpis.in_vorbereitung += 1;
    } else if (
      it.status === 'ausgeschrieben' ||
      it.status === 'verglichen' ||
      it.status === 'empfohlen' ||
      it.status === 'entschieden'
    ) {
      kpis.beim_kunden += 1;
    }
  }
  return kpis;
}

// ── Firm-wide list: keyset paging + server-side view filter + per-chip counts (#14A) ────

/** The server-side view filter of the firm-wide list = the web's status chips. */
export const PLACEMENT_LIST_VIEW_VALUES = ['alle', 'offen', 'erledigt', 'abgebrochen'] as const;
export type PlacementListView = (typeof PLACEMENT_LIST_VIEW_VALUES)[number];

export const PLACEMENT_FIRM_LIST_DEFAULT_LIMIT = 50;
export const PLACEMENT_FIRM_LIST_MAX_LIMIT = 200;

/** The firm-wide status chips (value + label). Moved here from AusschreibungenTab.tsx so the api
 *  (SQL funnel) and the web (chip row) share one source. Re-exported from that tab for callers. */
export const PLACEMENT_STATUS_FILTER_CHIPS: { value: PlacementListView; label: string }[] = [
  { value: 'alle', label: 'Alle' },
  { value: 'offen', label: 'Offen' },
  { value: 'erledigt', label: 'Erledigt' },
  { value: 'abgebrochen', label: 'Abgebrochen' },
];

/** The open/closed view filter — the same funnel as placementKpisFromItems + the api's count SQL. */
export function matchesPlacementFilter(item: PlacementListItem, view: PlacementListView): boolean {
  switch (view) {
    case 'alle':
      return true;
    case 'offen':
      return item.closure_status === 'offen' && item.status !== 'abgebrochen';
    case 'erledigt':
      return item.closure_status === 'erledigt';
    case 'abgebrochen':
      return item.status === 'abgebrochen';
  }
}

/** Per-chip counts over the caller's WHOLE visible tenant list (not the page). */
export interface PlacementListViewCounts {
  alle: number;
  offen: number;
  erledigt: number;
  abgebrochen: number;
}

/** GET /placements with NO subject: the firm-wide, PAGED envelope. `kpis` + `view_counts` come
 *  only on the first page (no cursor); later pages omit them. */
export interface PlacementFirmListResponse {
  subject_type: null;
  subject_id: null;
  items: PlacementListItem[];
  /** KPIs over the whole visible list (same funnel as placementKpisFromItems), computed in SQL. */
  kpis?: PlacementListKpis;
  view_counts?: PlacementListViewCounts;
  page: PageMeta;
}

/**
 * The firm-wide list's keyset cursor: the compound `<created_at>|<uuid>` of pagination.ts
 * (ORDER BY created_at DESC, id DESC, idx_placements_tenant_created). Placements only take
 * the compound form, never a bare timestamp.
 */
export function encodePlacementCursor(item: Pick<PlacementListItem, 'created_at' | 'id'>): string {
  return encodeKeysetCursor(item.created_at, item.id);
}

export function parsePlacementCursor(s: string | undefined | null): { createdAt: string; id: string } | null {
  const c = parseKeysetCursor(s);
  return c?.id ? { createdAt: c.at, id: c.id } : null;
}
